import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DATE_TIME_FORMAT, MAX_FILE_SIZE } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTagBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import {
    AuthService,
    PermissionType,
    AccessType,
    DataSources,
    EmptyCheckInDto,
    UpdateEmptyCheckInCommand,
    EmptyCheckInsClient,
    ShuntEmptyCheckInCommand,
    EmptyCheckInAction,
    CreateEmptyCheckInCommand,
    EmptyCheckInStatus,
    ShuntLocationType,
    TrailerType,
    CheckOutEmptyCheckInCommand,
} from '@services';
import EnumUtils from '../../../../../shared/enum-utils';
import notify from 'devextreme/ui/notify';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { EmptyCheckInToolbarComponent } from './components/empty-check-in-toolbar/empty-check-in-toolbar.component';
import { EmptyCheckInStatusPipe } from 'src/app/pipes/empty-check-in-status.pipe';
import { CustomStore } from 'devextreme-aspnet-data-nojquery';
import { NotesComponent, NoteType } from 'src/app/components/library/notes/notes.component';
import { ShuntMoveFormComponent } from 'src/app/pages/yard-management/shunt-management/shunt-move-form/shunt-move-form.component';

@Component({
    selector: 'app-empty-check-in-details',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        DxCheckBoxModule,
        EmptyCheckInToolbarComponent,
        ShuntMoveFormComponent,
        EmptyCheckInStatusPipe,
        RouterLink,
        NotesComponent,
    ],
    providers: [EmptyCheckInStatusPipe],
    templateUrl: './empty-check-in-details.component.html',
})
export class EmptyCheckInDetailsComponent implements OnInit {
    @Input() number: string | null = null;
    @ViewChild('validationGroup', { static: true }) validationGroup: DxValidationGroupComponent;
    @ViewChild(ShuntMoveFormComponent, { static: false }) createShuntMoveForm: ShuntMoveFormComponent;
    @ViewChild(DxScrollViewComponent, { static: false }) scrollView?: DxScrollViewComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    private route = inject(ActivatedRoute);
    private location = inject(Location);
    private router = inject(Router);
    protected auth = inject(AuthService);
    protected dataSources = inject(DataSources);
    private emptyCheckInClient = inject(EmptyCheckInsClient);
    protected readonly NoteType = NoteType;
    public dateTimeFormat = DATE_TIME_FORMAT;
    protected carrierDataSource = this.dataSources.carrierOptions();
    protected shipmentDataSource = this.dataSources.shipmentOptions();
    protected employeeDataSource = this.dataSources.unloadEmployeeOptions();
    protected emptyCheckIn: EmptyCheckInDto = new EmptyCheckInDto();
    protected readonly EmptyCheckInStatus = EmptyCheckInStatus;

    isLoading: boolean = false;
    isSubmitting: boolean = false;
    isReadOnly: boolean = false;
    maxFileSize: number = MAX_FILE_SIZE;
    isAddShuntMovePopupOpened = false;
    pendingNoteAction: EmptyCheckInAction | null = null;
    shuntLocationDataSource: CustomStore;
    shuntLocationOptions = EnumUtils.getEnumOptions(ShuntLocationType);
    trailerTypeOptions = EnumUtils.getEnumOptions(TrailerType);
    shuntLocation = ShuntLocationType;

    ngOnInit(): void {
        this.number = this.route.snapshot.paramMap.get('number');
        this.isReadOnly = !this.auth.checkPermission(PermissionType.EmptyCheckInDetails, AccessType.Edit);

        if (this.number) {
            this.loadEmptyCheckIn();
        } else {
            this.emptyCheckIn = new EmptyCheckInDto();
        }
    }

    private loadEmptyCheckIn() {
        this.isLoading = true;
        this.emptyCheckInClient.getEmptyCheckIn(this.number!).subscribe({
            next: async (data) => {
                this.emptyCheckIn = data;
                this.isReadOnly = this.isReadOnly ||
                    this.emptyCheckIn.status === EmptyCheckInStatus.OutboundEmpty ||
                    this.emptyCheckIn.status === EmptyCheckInStatus.OutboundLoaded;
                this.shipmentDataSource = this.dataSources.shipmentOptions(data.shipments ?? []);
                this.isLoading = false;
                this.loadShuntLocations();

                const canEdit = this.auth.checkPermission(PermissionType.EmptyCheckInDetails, AccessType.Manage)
                        || (this.emptyCheckIn.status === EmptyCheckInStatus.Draft && this.auth.checkPermission(PermissionType.EmptyCheckInDetails, AccessType.Edit));
                        
                this.isReadOnly = !canEdit;
            },
            error: () => {
                notify('Failed to load empty check-in', 'error', 3000);
                this.isLoading = false;
                this.location.back();
            },
        });
    }

    onLocationTypeChanged(e: any) {
        this.onValueChanged(undefined);
        this.loadShuntLocations();
    }

    loadShuntLocations() {
        this.shuntLocationDataSource = this.dataSources.shuntLocationOptions(
            this.emptyCheckIn.location as ShuntLocationType,
            this.emptyCheckIn.shuntLocationId,
        );
    }

    onValueChanged(shuntLocationId: any | undefined) {
        this.emptyCheckIn.shuntLocationId = shuntLocationId;
    }

    protected getBadgeClass(status: EmptyCheckInStatus): string {
        return EnumUtils.getEmptyCheckInStatusBadgeClass(status);
    }

    addShuntMove() {
        this.isAddShuntMovePopupOpened = true;
    }

    onShuntMovePopupSaveClick() {
        this.closePopupAndSave(EmptyCheckInAction.ShuntMove, () => {
            this.isAddShuntMovePopupOpened = false;
        });
    }

    private closePopupAndSave(
        action: EmptyCheckInAction,
        closePopup: () => void,
    ) {
        closePopup();
        this.save(action);
    }

    async onSaveClick(action: EmptyCheckInAction) {     
        if(action === EmptyCheckInAction.Investigate ||
             action === EmptyCheckInAction.InvestigationCompleted ||
              action === EmptyCheckInAction.CancelMove)
        {
            this.pendingNoteAction = action;
            this.notesComponent.openCreatePopup();

        } else{
            this.save(action);
        }
    }

    onNoteSaved() {
        if (!this.pendingNoteAction)
        {
            return;
        }

        const action = this.pendingNoteAction;
        this.pendingNoteAction = null;
        this.save(action);
    }

    onNoteCanceled() {
        this.pendingNoteAction = null;
    }

    async save(action: EmptyCheckInAction) {
        const result = this.validationGroup.instance.validate();

        if (!result.isValid) {
            notify(
                {
                    message: 'Please fix validation errors',
                    position: 'bottom center',
                },
                'error',
                3000,
            );
            return;
        }

        this.isSubmitting = true;

        const request$ = this.buildRequest$(action);

        request$.subscribe({
            next: async (number) => {
                notify(
                    this.number
                        ? 'Empty check-in updated successfully'
                        : 'Empty check-in created successfully',
                    'success',
                    3000,
                );
                this.isSubmitting = false;
                if (action === EmptyCheckInAction.CheckOut) {
                    await this.router.navigate(
                        ['/outbound/empty-check-outs/details', number],
                        {
                            replaceUrl: true,
                        },
                    );
                } else if (this.number) {
                    this.loadEmptyCheckIn();
                } else {
                    if(this.notesComponent)
                    {
                        await this.notesComponent.saveQueuedNotes(number);
                    }
                    
                    await this.router.navigate(
                        ['/inbound/empty-check-ins/details', number],
                        {
                            replaceUrl: true,
                        },
                    );
                }
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message ||
                    (this.number
                        ? 'Failed to update empty check-in'
                        : 'Failed to create empty check-in');
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            },
        });
    }

    private buildRequest$(action: EmptyCheckInAction) {
        const number = this.number;

        if (number === null) {
            const command = new CreateEmptyCheckInCommand({
                ...this.emptyCheckIn,
                action,
            });
            return this.emptyCheckInClient.createEmptyCheckIn(command);
        }

        switch (action) {
            case EmptyCheckInAction.ShuntMove: {
                const shuntMoveData = this.createShuntMoveForm.getData();
                const command = new ShuntEmptyCheckInCommand({
                    ...this.emptyCheckIn,
                    shuntMoveLocation: shuntMoveData.location,
                    shuntMoveLocationId: shuntMoveData.locationId,
                    priority: shuntMoveData.priority,
                    note: shuntMoveData.note,
                    number: number,
                    action,
                });
                return this.emptyCheckInClient.shuntEmptyCheckIn(command);
            }
            case EmptyCheckInAction.CheckOut: {
                const command = new CheckOutEmptyCheckInCommand({
                    ...this.emptyCheckIn,
                    number: number,
                    action,
                });
                return this.emptyCheckInClient.checkOutEmptyCheckIn(command);
            }
            default: {
                const command = new UpdateEmptyCheckInCommand({
                    ...this.emptyCheckIn,
                    number: number,
                    action,
                });
                return this.emptyCheckInClient.updateEmptyCheckIn(command);
            }
        }
    }

    cancel() {
        this.location.back();
    }
}
